/**
 * Keeps track of localization changes. It expects to be passed a config with
 * `localization`, which is a map of languages to an object containing the
 * localized strings. It initially loads the language preference from localStorage,
 * but once a person is loged in, their language preferences are fetched instead.
 * @type {Model}
 */

var Model = require("./types/Model.js");
var isObject = require("isobject");
var xtend = require("xtend");
// eslint-disable-next-line node/no-missing-require
const AsyncStorage = require("@react-native-async-storage/async-storage").default;

module.exports = Model({
	name: "localization",
	init: init,
	triggers: triggers,
	listeners: listeners,
	set_localization: set_localization
});

/**
 * Sets up listeners for personal data updates, and for triggers for setting the language.
 * Fetches the language preference in localStorage and attempts to load that language.
 */
function init() {

	get_language().then(set_localization.bind(this));

	function get_language() {
		return AsyncStorage.getItem("local")
			.then(function (language) {
				if (!language)  return "en";
				return language;
			})
			.catch(function () {
				return "en";
			});
	}
}

function triggers() {
	return {
		"set": set_localization
	};
}

function listeners() {
	return {
		"personal:updated": handle_personal_updated,
		"querystring:updated": handle_querystring_update
	};
}

/**
 * Triggered by the PersonalData model updating. Fetches out the language preference and sets it.
 * @param  {Object} personal The state of PersonalData
 */
function handle_personal_updated(personal) {
	var language = personal.language;
	if (language) this.set_localization(language);
}

/**
 * This gets called when the querystring from the URL gets parsed into an object
 * @param  {Object} query May contain the `lang` property for what language should be used
 */
function handle_querystring_update(query) {
	var language = query.lang;
	if (language)
		this.set_localization(language);
}

/**
 * Sets the localization preference to a certain language.
 * @param {Object|String} language Takes an object with a key `localization` or just a string. The value should be a language code. E.g. `en`, `fr`
 */
function set_localization(language) {
	var default_language = "en";

	if (isObject(language)) language = language.language;
	if (!language) language = default_language;

	// Attempt to save localization locally
	AsyncStorage.setItem("local", language).catch(function () {
		console.log("Unable to save preferred localization locally.");
	});

	var available = this.config.localization || {};
	var default_localization = available[default_language];
	var selected_localization = available[language];
	var localization = xtend(default_localization, selected_localization);

	return this.push({
		localization: localization,
		language: language
	});
}
